import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { useAuth } from './AuthContext';
import { useBilling } from './contexts/BillingContext';
import { getEntitlements } from './utils/billingEntitlements';
import LoadingSpinner from './components/shared/LoadingSpinner';
import TrialExpiredPaywall from './components/billing/TrialExpiredPaywall';

// Billing-exempt paths — always accessible regardless of billing state.
// Settings must be reachable so the user can fix their situation.
const BILLING_EXEMPT_PATHS = new Set(['/settings', '/m/settings', '/billing/success']);
const BILLING_EXEMPT_PREFIXES = ['/settings', '/m/settings'];

const ProtectedRoute = ({ children, requireOnboarding = true }) => {
  const { isAuthenticated, loading, onboardingComplete, onboardingStep, features } = useAuth();
  // Read live billing state from BillingContext — it updates immediately after
  // PaymentForm.refresh(), so the gate re-evaluates without a page reload.
  const { billingStatus, initialized: billingInitialized } = useBilling();
  const location = useLocation();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireOnboarding && !onboardingComplete) {
    return <Navigate to={`/onboarding?step=${onboardingStep || 1}`} replace />;
  }

  // Billing gate — only applies after onboarding is complete.
  // Wait for BillingContext to initialize before making the block decision;
  // showing a spinner here prevents premature access grants during the load window.
  if (requireOnboarding && onboardingComplete) {
    if (!billingInitialized) {
      return <LoadingSpinner />;
    }

    const isBillingExempt = BILLING_EXEMPT_PATHS.has(location.pathname) ||
      BILLING_EXEMPT_PREFIXES.some((prefix) => location.pathname.startsWith(prefix));

    if (!isBillingExempt && features?.reviewAccess !== true) {
      const entitlements = getEntitlements(billingStatus.accessState);
      const subscriptionRequired = billingStatus.accessState === 'not_provisioned';
      if (!entitlements.canAccessPremium || subscriptionRequired) {
        let mode = subscriptionRequired ? 'subscription_required' : 'trial_expired';
        if (!subscriptionRequired && entitlements.isCanceled) {
          mode = 'canceled';
        } else if (!subscriptionRequired && entitlements.isBillingBlocked) {
          mode = 'payment_issue';
        } else if (!subscriptionRequired && entitlements.isBillingUnavailable) {
          mode = 'billing_unavailable';
        }
        return (
          <TrialExpiredPaywall mode={mode} />
        );
      }
    }
  }

  return children ?? <Outlet />;
};

export default ProtectedRoute;
